import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/** Each visible offer adds exactly its displayed products with one explicit click. */
class KunnieProductPairing extends Component {
  #controller;
  #busy = false;
  #blocked = false;
  #confirmed = false;
  #variantPending = false;
  #catalogUnavailable = false;
  #selectionSerial = 0;
  #activeOffer;
  #copy;
  #key;
  #observer;
  #visible = false;
  #viewed = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#copy = JSON.parse(this.querySelector('[data-pair-copy]').textContent);
    this.#key = 'kunnie:pair:' + this.dataset.product + ':' + this.dataset.presentation;
    try {
      const saved = sessionStorage.getItem(this.#key);
      this.#blocked = !!saved;
      if (saved) {
        try { const state = JSON.parse(saved); this.#confirmed = state.confirmed === true; this.#activeOffer = state.offer; } catch { /* Legacy or uncertain guard stays blocked. */ }
      }
    } catch { /* Storage must succeed before POST. */ }
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-offer-add]');
      if (button && this.contains(button)) this.#add(button.closest('[data-pair-offer]'));
    }, options);
    document.addEventListener('shopify:product:select', async (event) => {
      if (String(event.product?.id).split('/').pop() !== this.dataset.product || event.target.closest?.('product-card, quick-add-dialog')) return;
      const serial = ++this.#selectionSerial;
      this.#variantPending = true;
      this.#paint();
      try {
        const result = await event.promise;
        if (!this.isConnected || serial !== this.#selectionSerial) return;
        const fresh = result?.detail?.html?.querySelector('kunnie-product-pairing[data-product="' + this.dataset.product + '"]');
        if (fresh) this.#replace(fresh);
        else this.#catalogUnavailable = true;
      } catch { /* The native picker owns its error feedback. */ }
      finally {
        if (this.isConnected && serial === this.#selectionSerial) { this.#variantPending = false; this.#paint(); }
      }
    }, options);
    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.25);
      this.#view();
    }, { threshold: 0.25 });
    this.#observer.observe(this);
    document.addEventListener('visitorConsentCollected', () => queueMicrotask(() => { if (this.isConnected) this.#view(); }), options);
    this.#paint();
    if (this.#blocked) this.#status(this.#confirmed ? this.#copy.added : this.#copy.error);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }

  #offers() { return [...this.querySelectorAll('[data-pair-offer]')]; }
  #lines(offer) { return [...(offer?.querySelectorAll('[data-offer-line]') || [])]; }
  #find(root, id) { return root.querySelector('[data-offer-id="' + CSS.escape(id) + '"]'); }
  #details(offer) {
    const offered = this.#lines(this.#offers()[0]).filter((line) => line.dataset.match !== 'anchor');
    const lines = this.#lines(offer || this.#offers()[0]);
    return {
      product_id: this.dataset.product,
      presentation: this.dataset.presentation,
      bundle_product_id: offer?.dataset.bundleProduct,
      bundle_variant_id: offer?.dataset.bundleVariant,
      discount_percent: Number(offer?.dataset.bundlePercent || 0),
      target_discount_percent: Number(offer?.dataset.bundleTargetPercent || 0),
      price_rounding: offer?.dataset.bundleRounding,
      offer_id: offer?.dataset.offerId || 'overview',
      variant_ids: lines.map((line) => line.dataset.variant),
      offered_matches: offered.map((line, index) => (index + 1) + ':' + line.dataset.match + ':' + line.dataset.revision),
      match_ids: offered.map((line) => line.dataset.match),
      selected_matches: offer ? lines.filter((line) => line.dataset.match !== 'anchor').map((line) => line.dataset.match + ':' + line.dataset.revision) : [],
      currency: this.dataset.currency,
      value: (offer?.dataset.bundlePrice ? Number(offer.dataset.bundlePrice) : lines.reduce((sum, line) => sum + Number(line.dataset.price), 0)) / 100,
      individual_value: lines.reduce((sum, line) => sum + Number(line.dataset.price), 0) / 100,
    };
  }

  #track(name, offer) { return window.kunnieAnalytics?.track(name, this.#details(offer)); }
  #view() {
    if (!this.#viewed && this.#visible && this.#track('pdp_match_viewed') === true) { this.#viewed = true; this.#observer?.disconnect(); }
  }
  #status(text) { this.querySelector('[data-pair-status]').textContent = text; }

  #paint() {
    const money = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: this.dataset.currency });
    for (const offer of this.#offers()) {
      const lines = this.#lines(offer);
      const individual = lines.reduce((sum, line) => sum + Number(line.dataset.price), 0);
      const total = offer.dataset.bundlePrice ? Number(offer.dataset.bundlePrice) : individual;
      for (const line of lines) line.querySelector('[data-line-price]').textContent = money.format(Number(line.dataset.price) / 100);
      offer.querySelector('[data-offer-total]').textContent = money.format(total / 100);
      const individualPrice = offer.querySelector('[data-offer-individual]');
      if (individualPrice) individualPrice.textContent = money.format(individual / 100);
      const saving = offer.querySelector('[data-offer-saving]');
      if (saving && this.#copy.saving) saving.textContent = this.#copy.saving.replace('__amount__', money.format((individual - total) / 100));
      const button = offer.querySelector('[data-offer-add]');
      if (!button.dataset.label) button.dataset.label = button.textContent;
      const active = this.#activeOffer === offer.dataset.offerId;
      button.textContent = active && this.#busy ? this.#copy.adding : active && this.#confirmed ? this.#copy.added_button : button.dataset.label;
      button.disabled = this.#busy || this.#blocked || this.#variantPending || this.#catalogUnavailable || lines.length < 2 || lines.some((line) => line.dataset.available !== 'true') || (!!offer.dataset.bundleVariant && offer.dataset.bundleAvailable !== 'true');
      button.setAttribute('aria-busy', String(active && this.#busy));
    }
    this.querySelector('[data-pair-cart]').hidden = !this.#blocked;
  }

  #replace(fresh) {
    const list = fresh.querySelector('[data-offer-list]');
    if (!list) return;
    this.querySelector('[data-offer-list]').replaceChildren(...list.childNodes);
    this.dataset.currency = fresh.dataset.currency;
    this.dataset.variant = fresh.dataset.variant;
    this.#copy = JSON.parse(fresh.querySelector('[data-pair-copy]').textContent);
    this.#catalogUnavailable = false;
    this.#paint();
  }

  async #add(offer) {
    if (!offer || this.#busy || this.#blocked || this.#variantPending || offer.querySelector('[data-offer-add]').disabled) return;
    const serial = this.#selectionSerial;
    const offerId = offer.dataset.offerId;
    const chosen = this.#lines(offer).map((line) => ({ ...line.dataset }));
    const bundle = { ...offer.dataset };
    this.#activeOffer = offerId;
    this.#busy = true;
    this.#status('');
    this.#paint();
    this.#track('pdp_match_selection', offer);
    let submitted = false;
    try {
      const url = new URL(location.href);
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.set('variant', this.dataset.variant); // the open PDP's own selection, also for sets anchored elsewhere
      const response = await fetch(url, { cache: 'no-store', signal: this.#controller.signal });
      if (!response.ok) throw new Error('catalog');
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('kunnie-product-pairing');
      if (!fresh) { this.#catalogUnavailable = true; throw new Error('catalog'); }
      if (serial !== this.#selectionSerial) throw new Error('selection_changed');
      const freshOffer = this.#find(fresh, offerId);
      const freshLines = this.#lines(freshOffer);
      const changed = fresh.dataset.currency !== this.dataset.currency || freshOffer?.dataset.bundleVariant !== bundle.bundleVariant || freshOffer?.dataset.bundlePrice !== bundle.bundlePrice || freshOffer?.dataset.bundlePercent !== bundle.bundlePercent || freshOffer?.dataset.bundleTargetPercent !== bundle.bundleTargetPercent || freshOffer?.dataset.bundleRounding !== bundle.bundleRounding || freshOffer?.dataset.bundleRevision !== bundle.bundleRevision || (!!bundle.bundleVariant && freshOffer?.dataset.bundleAvailable !== 'true') || freshLines.length !== chosen.length || chosen.some((line, index) => {
        const next = freshLines[index]?.dataset;
        return !next || next.variant !== line.variant || next.price !== line.price || next.available !== 'true' || next.match !== line.match || next.revision !== line.revision;
      });
      if (changed) {
        this.#replace(fresh);
        this.#busy = false;
        this.#paint();
        this.#status(this.#copy.changed);
        (this.#find(this, offerId) || this.#offers()[0])?.querySelector('[data-offer-add]').focus({ preventScroll: true });
        return;
      }
      const cartRead = async () => {
        const response = await fetch(Theme.routes.cart_url + '.js', { credentials: 'same-origin', cache: 'no-store', signal: this.#controller.signal });
        if (!response.ok) throw new Error('cart');
        return response.json();
      };
      const before = await cartRead();
      if (before.currency !== this.dataset.currency) throw new Error('currency');
      if (serial !== this.#selectionSerial) throw new Error('selection_changed');
      const transaction = crypto.randomUUID();
      const cartLines = bundle.bundleVariant ? [{ variant: bundle.bundleVariant, match: offerId, revision: bundle.bundleRevision }] : chosen;
      const items = cartLines.map((line) => ({ id: line.variant, quantity: 1, properties: {
        _kunnie_match_group: transaction,
        _kunnie_match_anchor: this.dataset.product,
        _kunnie_match: line.match,
        _kunnie_match_revision: line.revision,
        _kunnie_match_offer: offerId,
        _kunnie_match_presentation: this.dataset.presentation,
        ...(bundle.bundleVariant ? { _kunnie_match_components: JSON.stringify(chosen.map((component) => ({ variant: component.variant, match: component.match, revision: component.revision }))) } : {}),
      } }));
      const sections = [...new Set([...(bundle.bundleVariant ? ['kunnie-cart-bundle-proof'] : []), ...[...document.querySelectorAll('cart-items-component')].map((element) => element.dataset.sectionId).filter(Boolean)])].slice(0, 5);
      sessionStorage.setItem(this.#key, transaction);
      this.#blocked = true;
      submitted = true;
      const added = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }), signal: this.#controller.signal,
      });
      const data = await added.json();
      const cart = await cartRead();
      const proofElement = new DOMParser().parseFromString(data.sections?.['kunnie-cart-bundle-proof'] || '', 'text/html').querySelector('[data-kunnie-cart-bundles]');
      const proof = proofElement ? JSON.parse(proofElement.textContent) : null;
      const confirmed = added.ok && !data.status && items.every((item) => cart.items.some((line) => {
        if (String(line.variant_id) !== item.id || line.quantity !== 1 || line.properties?._kunnie_match_group !== transaction) return false;
        if (!bundle.bundleVariant) return true;
        const components = proof?.items?.find((item) => item.key === line.key)?.components || [];
        return line.final_line_price === Number(bundle.bundlePrice) && components.length === chosen.length && chosen.every((component) => components.some((part) => String(part.variant_id) === component.variant && part.quantity === 1));
      }));
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: confirmed ? 'add' : 'update', context: 'product', lines: items.map((item) => ({ merchandiseId: item.id, quantity: 1 })), promise: deferred.promise }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: data.sections, source: 'kunnie-product-pairing', didError: !confirmed } });
      if (!confirmed) throw new Error('unconfirmed');
      this.#confirmed = true;
      try { sessionStorage.setItem(this.#key, JSON.stringify({ id: transaction, offer: offerId, confirmed: true })); } catch { /* The pre-POST guard already exists. */ }
      this.#track('pdp_match_added', offer);
      this.#status(this.#copy.added);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#status(submitted ? this.#copy.error : error.message === 'selection_changed' ? this.#copy.changed : this.#copy.unavailable);
        this.#track('pdp_match_error', offer);
      }
    } finally {
      this.#busy = false;
      if (this.isConnected) this.#paint();
    }
  }
}

if (!customElements.get('kunnie-product-pairing')) customElements.define('kunnie-product-pairing', KunnieProductPairing);
